<script lang="ts">
  /**
   * ClubMembersStrip — the Club tab's opener (spec 2b, contract C5).
   *
   * One row per member, self first then by name. Each row: name, this page's
   * totals (swims · distance · minutes), and seven daily distance bars. The bars
   * live in ONE horizontal scroller with one snap page per CALENDAR WEEK (build 15:
   * the locale's first weekday, 2 or 3 weeks covering the 14-day window) that every
   * row shares, so the rows move together under sticky row heads. Columns read
   * "T 8"; days after today are dimmed and inert.
   * A bar tap SELECTS its day (the parent moves the date row, B-025); it opens nothing.
   * When the date row moves to a day on another week, the strip pages there.
   *
   * Bars are distances on ONE shared scale (the club's biggest day), never a
   * rank. No scores, no leaderboard, by rule.
   */
  import { onMount, untrack } from "svelte";
  import type { ClubMember, ClubReaction, ClubSnapshot, ClubSwim } from "../lib/club/snapshot";
  import {
    badgeEmoji,
    barsFor,
    dayKeyDate,
    maxDayDistance,
    pageFor,
    pageIndexOf,
    reactionsForSwim,
    sortMembers,
    stripIsEmpty,
    weekPages,
    weekTotalsFor,
  } from "../lib/club/strip";
  import { fmtMeters, formatDuration } from "../lib/format";
  import { haptic } from "../lib/haptics";

  interface Props {
    snapshot: ClubSnapshot;
    /** What "today" is: the live clock on a shell, `generatedAt` for a fixture. */
    now: number;
    /** The locale's first weekday, ISO 1-7 (7 = Sunday, the default). */
    firstDay?: number;
    /** A bar selects its DAY (every swim on it, newest first); the parent moves the date row. */
    onSelectDay?: (swims: ClubSwim[]) => void;
    /** The date row's day: its bars read as selected, and the strip pages to its week. */
    selectedDay?: string | null;
    /** Reactions to show on bars: ALREADY filtered to the fresh 24 h (the parent
     *  owns the clock and the optimistic taps). Defaults to none. */
    reactions?: readonly ClubReaction[];
  }
  let { snapshot, now, firstDay = 7, onSelectDay, selectedDay = null, reactions = [] }: Props = $props();

  /** The badge on a bar: your own emoji, else the most-given, and the total,
   *  over EVERY swim on that day. */
  function badgeFor(swims: ClubSwim[]): { emoji: string; total: number } | null {
    if (swims.length === 0) return null;
    const s = reactionsForSwim(reactions, swims.map((x) => x.id), snapshot.self);
    const emoji = badgeEmoji(s);
    return emoji ? { emoji, total: s.total } : null;
  }

  let members = $derived(sortMembers(snapshot.members, snapshot.self));
  // VP round 1: the date row's day is always on a page, any distance back (its week joins the pages).
  let pages = $derived(weekPages(now, firstDay, selectedDay));
  let hasSelection = $derived(selectedDay !== null && pageIndexOf(pages, selectedDay) >= 0);
  let newest = $derived(pages.length - 1);
  let maxM = $derived(maxDayDistance(members, pages));
  let empty = $derived(stripIsEmpty(snapshot));

  // ONE scroller for every row (measured 2026-09-10: per-row scrollers synced
  // after a settle timer left the second row still for ~0.7 s during a touch
  // fling). Rows share the track, so they move in lockstep by construction;
  // the row heads stick to the left edge. `page` only drives the label + totals.
  // It opens on the date row's week when the strip shows that day, else this week, and re-resolves
  // whenever the WEEKS change (a live snapshot across midnight can turn 3 pages into 2: a kept
  // index then read a page that was gone). Paging by hand or by the date row assigns it.
  let pageKey = $derived(pages.map((p) => p.days[0].key).join(","));
  let page = $derived.by(() => {
    pageKey;
    return untrack(() => pageFor(pages, selectedDay));
  });
  let scroller = $state<HTMLDivElement | null>(null);
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  function pageOf(el: HTMLDivElement): number {
    return el.clientWidth > 0 ? Math.round(el.scrollLeft / el.clientWidth) : page;
  }

  function onScroll(e: Event): void {
    const el = e.currentTarget as HTMLDivElement;
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      const p = pageOf(el);
      if (p !== page) {
        page = p;
        haptic("selection");
      }
    }, 120);
  }

  onMount(() => () => {
    if (settleTimer) clearTimeout(settleTimer);
  });

  // Land on the resolved page without a scroll animation (jump, not glide): on mount, and again
  // whenever the weeks change under an open tab.
  $effect(() => {
    pageKey;
    untrack(() => {
      if (scroller) scroller.scrollLeft = page * scroller.clientWidth;
    });
  });

  function scrollToPage(p: number): void {
    scroller?.scrollTo({ left: p * scroller.clientWidth, behavior: "smooth" });
  }

  function goToPage(p: number): void {
    if (!scroller) return;
    haptic("selection");
    scrollToPage(p);
  }

  // Build 15: the strip follows the date row across a week boundary. The effect tracks only
  // `selectedDay`, so paging by hand to look at another week is never undone.
  $effect(() => {
    const day = selectedDay;
    untrack(() => {
      const i = day ? pageIndexOf(pages, day) : -1;
      if (i < 0 || i === page) return;
      page = i;
      scrollToPage(i);
    });
  });

  function barHeight(distanceM: number): number {
    if (distanceM <= 0) return 0;
    return Math.max(4, Math.round((56 * distanceM) / maxM));
  }

  function totalsLine(member: ClubMember): string {
    const t = weekTotalsFor(member, pages[page]);
    // Scope in the words (VP round 1, fix 5): "No swims" next to the roster's
    // all-time count read as a contradiction.
    if (t.swims === 0) return page === newest ? "No swims this week" : "No swims that week";
    return `${t.swims} ${t.swims === 1 ? "swim" : "swims"} · ${fmtMeters(t.distanceM)} · ${formatDuration(t.durationSec)}`;
  }

  function dayLabel(key: string): string {
    // "YYYY-MM-DD" -> "Tue Sep 8", local.
    return dayKeyDate(key).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }

  function selectBar(swims: ClubSwim[]): void {
    if (swims.length === 0) return;
    haptic("light");
    onSelectDay?.(swims);
  }

  /** What the bar announces: one swim, or the day's N swims in all. */
  function barLabel(member: ClubMember, bar: { day: { key: string }; distanceM: number; swims: ClubSwim[] }, badge: { total: number } | null): string {
    const n = bar.swims.length;
    const what = n > 1 ? `${n} swims, ${fmtMeters(bar.distanceM)} in all` : fmtMeters(bar.distanceM);
    const r = badge ? `, ${badge.total} ${badge.total === 1 ? "reaction" : "reactions"}` : "";
    return `${member.name}, ${dayLabel(bar.day.key)}, ${what}${r}. Show this day`;
  }
</script>

<section class="strip" class:has-selection={hasSelection} data-testid="club-strip" aria-label="Club members, by week">
  <div class="strip-head">
    <span class="week-label" data-testid="club-strip-week">{pages[page].label}</span>
    <!-- B-031: a visible paging affordance, a real button so it is never a dead tap; dragging
         still pages. Build 15: "‹ Last week" (or "‹ 2 weeks ago") steps back a week; the oldest
         page jumps to "This week ›". -->
    {#if page > 0}
      <button type="button" class="page-hint" data-testid="club-strip-page" aria-label={`Show ${pages[page - 1].label.toLowerCase()}`} onclick={() => goToPage(page - 1)}>‹ {pages[page - 1].label}</button>
    {:else}
      <button type="button" class="page-hint" data-testid="club-strip-page" aria-label="Show this week" onclick={() => goToPage(newest)}>This week ›</button>
    {/if}
  </div>

  <div
    class="scroller"
    data-testid="club-strip-scroller"
    bind:this={scroller}
    onscroll={onScroll}
    role="group"
    aria-label="Every member's daily distance by calendar week; drag sideways for another week"
  >
    <div class="track" style={`--pages:${pages.length}`}>
      {#each members as member (member.id)}
        {@const isSelf = member.id === snapshot.self}
        <div class="row" data-testid="club-member-row" data-member-id={member.id}>
          <div class="row-head">
            <span class="name">{member.name}{#if isSelf}<small>you</small>{/if}</span>
            <span class="totals" data-testid="club-member-totals">{totalsLine(member)}</span>
          </div>
          <div class="pages">
            {#each pages as p (p.index)}
              <div class="page" data-page={p.index}>
                {#each barsFor(member, p) as bar (bar.day.key)}
                  {@const letter = `${bar.day.letter} ${bar.day.dayNumber}`}
                  {#if bar.swims.length > 0 && !bar.day.isFuture}
                    {@const badge = badgeFor(bar.swims)}
                    <button
                      type="button"
                      class="day has-swim"
                      class:selected={bar.day.key === selectedDay}
                      data-testid="club-day-bar"
                      data-day={bar.day.key}
                      data-swim-id={bar.swims[0].id}
                      data-swim-count={bar.swims.length}
                      aria-label={barLabel(member, bar, badge)}
                      onclick={() => selectBar(bar.swims)}
                    >
                      <span class="bar-well"><span class="bar" style={`height:${barHeight(bar.distanceM)}px`}></span></span>
                      <span class="letter" class:today={bar.day.isToday}>{letter}</span>
                      <span class="badge" data-testid="club-bar-reactions" data-swim-id={bar.swims[0].id}>{#if badge}{badge.emoji}{#if badge.total > 1}<span class="badge-n">{badge.total}</span>{/if}{/if}</span>
                    </button>
                  {:else}
                    <!-- Build 15: a day after today is dimmed and inert (never a tap, even with a skewed swim). -->
                    <div
                      class="day"
                      class:future={bar.day.isFuture}
                      class:selected={bar.day.key === selectedDay}
                      data-day={bar.day.key}
                      aria-disabled={bar.day.isFuture ? "true" : undefined}
                      aria-hidden={bar.day.isFuture ? undefined : "true"}
                    >
                      <span class="bar-well"><span class="tick"></span></span>
                      <span class="letter" class:today={bar.day.isToday}>{letter}</span>
                      <span class="badge"></span>
                    </div>
                  {/if}
                {/each}
              </div>
            {/each}
          </div>
        </div>
      {/each}
    </div>
  </div>

  {#if empty}
    <!-- Names the invite; the button itself is the leader card's, right below
         (ClubTab leaderWaiting), so the page never shows two of them. -->
    <div class="empty" data-testid="club-strip-empty">
      <p class="empty-title">No clubmates yet.</p>
      <p class="empty-body">Tap <strong>Invite a clubmate</strong> below. Their swims line up under yours, day by day.</p>
    </div>
  {/if}
</section>

<style>
  .strip {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: 12px var(--space-4) var(--space-3);
  }
  .strip-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
  }
  .week-label {
    font: var(--type-caption);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .page-hint {
    font: var(--type-caption);
    color: var(--accent-primary);
    background: transparent;
    border: 0;
    padding: 6px 0 6px var(--space-3);
    margin: -6px 0;
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }
  .scroller {
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    /* Negative margin so a page spans the card's inner width edge to edge. */
    margin: 0 calc(-1 * var(--space-2));
  }
  .scroller::-webkit-scrollbar {
    display: none;
  }
  /* One viewport per week page; every row rides the same track. */
  .track {
    width: calc(var(--pages, 2) * 100%);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  /* One viewport wide, pinned to the left edge while the bars scroll under it. */
  .row-head {
    position: sticky;
    left: 0;
    width: calc(100% / var(--pages, 2));
    box-sizing: border-box;
    padding: 0 var(--space-2);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-3);
    min-width: 0;
  }
  .name {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .name small {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    margin-left: 6px;
    text-transform: uppercase;
  }
  .totals {
    font: var(--type-caption);
    color: var(--text-muted);
    white-space: nowrap;
    flex-shrink: 0;
  }
  .pages {
    display: grid;
    grid-template-columns: repeat(var(--pages, 2), 1fr);
  }
  .page {
    scroll-snap-align: start;
    scroll-snap-stop: always;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 0 var(--space-2);
    box-sizing: border-box;
  }
  .day {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    min-width: 0;
    padding: 2px 0 0;
    background: transparent;
    border: 0;
    border-radius: var(--radius-sm);
    font-family: var(--font-text);
    color: inherit;
  }
  .day.has-swim {
    cursor: pointer;
  }
  .day.has-swim:active {
    background: var(--bg-raised);
  }
  /* VP round 1 (blocker 2): the selected day must be obvious and distinct from today. Once a day is
     selected, every other bar dims, so the selected column's bars read as the filled ones. */
  .strip.has-selection .day.has-swim:not(.selected) .bar {
    opacity: 0.4;
  }
  .bar-well {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    height: 56px;
  }
  .bar {
    display: block;
    width: 60%;
    max-width: 22px;
    border-radius: 3px 3px 1px 1px;
    background: var(--accent-distance);
  }
  .tick {
    display: block;
    width: 60%;
    max-width: 22px;
    height: 2px;
    border-radius: 1px;
    background: var(--divider);
  }
  .letter {
    font: var(--type-caption);
    color: var(--text-muted);
    line-height: 12px;
    padding: 1px 4px;
    border: 1px solid transparent;
    border-radius: var(--radius-pill);
    white-space: nowrap;
  }
  /* Today: a ring. Selected: a filled pill with bold dark text (wins when today is selected). */
  .letter.today {
    color: var(--text-primary);
    border-color: var(--text-muted);
  }
  .day.selected .letter {
    background: var(--text-primary);
    border-color: var(--text-primary);
    color: var(--bg-canvas);
    font-weight: 700;
  }
  .day.future {
    opacity: 0.35;
  }
  /* Reaction badge under the letter: a fixed slot on every column so rows
     with and without reactions stay the same height. */
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    height: 14px;
    font-size: 11px;
    line-height: 1;
    color: var(--text-body);
  }
  .badge-n {
    font: var(--type-caption);
    font-size: 10px;
    color: var(--text-muted);
  }

  .empty {
    border-top: 1px solid var(--divider);
    padding-top: var(--space-3);
  }
  .empty-title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
  }
  .empty-body {
    font-size: 14px;
    line-height: 1.45;
    color: var(--text-body);
    margin: 4px 0 0;
  }
</style>
